import { useState } from 'react'
import styled from 'styled-components'
import { Carousel, Input, Button, message } from 'antd'
import {
  UserOutlined,
  KeyOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from '@ant-design/icons'
import logo from '../assets/sercacador_branco.png'
import { isEmpty } from 'lodash'
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth'

import { backgroundImage } from '../constants'

import { useNavigate } from 'react-location'

type ActiveScreen = 'login' | 'register' | 'recover'

const Login = () => {
  const [loginEmail, setLoginEmail] = useState<string>('')
  const [loginPassword, setLoginPassword] = useState<string>('')

  const [registerEmail, setRegisterEmail] = useState<string>('')
  const [registerPassword, setRegisterPassword] = useState<string>('')
  const [registerName, setRegisterName] = useState<string>('')

  const [recoverEmail, setRecoverEmail] = useState<string>('')

  const [activeScreen, setActiveScreen] = useState<ActiveScreen>('login')

  const auth = getAuth()

  const navigate = useNavigate()

  const triggerError = (errorCode: string) => {
    switch (errorCode) {
      case 'auth/email-already-exists':
      case 'auth/email-already-in-use':
        return message.error('O email já se encontra registado')
      case 'auth/invalid-email':
        return message.error('O email é inválido')
      case 'auth/invalid-password':
      case 'auth/weak-password':
        return message.error(
          'A password é inválida ou muito pequena. (Min. 6 caracteres)'
        )
      default:
        return message.error('Algo correu mal, tente novamente mais tarde')
    }
  }

  const onLoginClick = () => {
    setPersistence(auth, browserSessionPersistence)
      .then(() => {
        // Existing and future Auth states are now persisted in the current
        // session only. Closing the window would clear any existing state even
        // if a user forgets to sign out.
        // ...
        // New sign-in will be persisted with session persistence.
        return signInWithEmailAndPassword(auth, loginEmail, loginPassword)
          .then((userCredential) => {
            // Signed in
            const user = userCredential.user

            if (user.emailVerified) {
              message.success('Login efectuado', 1)
              navigate({ to: '/dashboard', replace: true })
            } else {
              message.error(
                'Tem que verificar o email de registo antes de entrar.'
              )
              message.info(
                <span>
                  Não recebeu o email?{' '}
                  <Button
                    type="primary"
                    onClick={() => {
                      sendEmailVerification(user)
                      message.success('Email de verificação enviado')
                    }}
                  >
                    Voltar a enviar
                  </Button>
                </span>,
                5
              )
            }
          })
          .catch((error) => {
            triggerError(error.code)
          })
      })
      .catch((error) => {
        // Handle Errors here.
        console.log('error presist:', error.code)
      })
  }

  const onRegisterClick = () => {
    if (isEmpty(registerName)) {
      message.error('Preencha o campo "Nome"')
      return
    }

    if (isEmpty(registerPassword)) {
      message.error('Preencha o campo "Password"')
      return
    }

    createUserWithEmailAndPassword(auth, registerEmail, registerPassword)
      .then((userCredential) => {
        // Registed
        const user = userCredential.user

        updateProfile(user, {
          displayName: registerName,
        })
          .then(() => {
            sendEmailVerification(user)
            backTo('login')
          })
          .catch((error) => {
            triggerError(error.code)
          })
      })
      .catch((error) => {
        triggerError(error.code)
      })
  }

  const onRecoverClick = () => {
    sendPasswordResetEmail(auth, recoverEmail)
    backTo('login')
    message.success(
      'Se o seu email existe na nossa plataforma deve receber um email para repor a password brevemente',
      4
    )
  }

  const backTo = (screen: ActiveScreen) => {
    setLoginEmail('')
    setLoginPassword('')
    setRegisterEmail('')
    setRegisterPassword('')
    setRecoverEmail('')
    setActiveScreen(screen)
  }

  const getActiveSection = () => {
    if (activeScreen === 'login') {
      return (
        <Form>
          <FormTitle>Login</FormTitle>
          <FormWrapper>
            <Input
              style={{ marginBottom: '20px' }}
              size="large"
              placeholder="Email"
              prefix={<UserOutlined />}
              value={loginEmail}
              onChange={(e) => setLoginEmail(String(e.target.value))}
            />
            <Input.Password
              style={{ marginBottom: '20px' }}
              size="large"
              placeholder="Password"
              prefix={<KeyOutlined />}
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
              value={loginPassword}
              onChange={(e) => setLoginPassword(String(e.target.value))}
            />
            <Button type="primary" onClick={onLoginClick}>
              Entrar
            </Button>
            <ExtraActionsWrapper>
              <ExtraButtonContainer>
                <StyledExtraText>Ainda não está registado?</StyledExtraText>
                <StyledButton
                  type="link"
                  onClick={() => setActiveScreen('register')}
                  size="small"
                >
                  Registar
                </StyledButton>
              </ExtraButtonContainer>
              <ExtraButtonContainer>
                <StyledExtraText>Esqueceu-se da password?</StyledExtraText>
                <StyledButton
                  type="link"
                  onClick={() => setActiveScreen('recover')}
                  size="small"
                >
                  Recuperar Password
                </StyledButton>
              </ExtraButtonContainer>
            </ExtraActionsWrapper>
          </FormWrapper>
        </Form>
      )
    } else if (activeScreen === 'register') {
      return (
        <Form>
          <FormTitle>Registar</FormTitle>
          <FormWrapper>
            <Input
              type="text"
              style={{ marginBottom: '20px' }}
              size="large"
              placeholder="Nome"
              prefix={<UserOutlined />}
              value={registerName}
              onChange={(e) => setRegisterName(String(e.target.value))}
            />
            <Input
              type="text"
              style={{ marginBottom: '20px' }}
              size="large"
              placeholder="Email"
              prefix={<UserOutlined />}
              value={registerEmail}
              onChange={(e) => setRegisterEmail(String(e.target.value))}
            />
            <Input.Password
              style={{ marginBottom: '20px' }}
              size="large"
              placeholder="Password"
              prefix={<KeyOutlined />}
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
              value={registerPassword}
              onChange={(e) => setRegisterPassword(String(e.target.value))}
            />
            <Button
              type="primary"
              onClick={onRegisterClick}
              style={{ marginBottom: '20px' }}
            >
              Registar
            </Button>
            <Button type="default" onClick={() => backTo('login')}>
              Voltar ao Login
            </Button>
          </FormWrapper>
        </Form>
      )
    } else if (activeScreen === 'recover') {
      return (
        <Form>
          <FormTitle>Recuperar Password</FormTitle>
          <FormWrapper>
            <Input
              type="text"
              style={{ marginBottom: '20px' }}
              size="large"
              placeholder="Email"
              prefix={<UserOutlined />}
              value={recoverEmail}
              onChange={(e) => setRecoverEmail(String(e.target.value))}
            />
            <Button
              type="primary"
              onClick={onRecoverClick}
              style={{ marginBottom: '20px' }}
            >
              Recuperar
            </Button>
            <Button type="default" onClick={() => backTo('login')}>
              Voltar ao Login
            </Button>
          </FormWrapper>
        </Form>
      )
    } else {
      return null
    }
  }

  return (
    <Container>
      <Logo />
      <LoginBox>
        <LoginSlider>
          <StyledSlider autoplay adaptiveHeight>
            <ImageContainer>
              <img
                src="https://media.istockphoto.com/photos/hunter-and-his-dog-with-prey-at-sunset-picture-id1301911854?b=1&k=20&m=1301911854&s=170667a&w=0&h=x7itNoYGKwXPRAWS1t1GfmZ_MkKzwbwBB9tlI3KZJmE="
                alt="login1"
              />
            </ImageContainer>
            <ImageContainer>
              <img
                src="https://images.immediate.co.uk/production/volatile/sites/23/2016/07/GettyImages-965458476-4f434aa.jpg?quality=90&resize=768%2C574"
                alt="login3"
              />
            </ImageContainer>
            <ImageContainer>
              <img
                src="https://cdn.hiconsumption.com/wp-content/uploads/2018/10/Best-Hunting-Destinations-0-Hero.jpg"
                alt="login2"
              />
            </ImageContainer>
            <ImageContainer>
              <img
                src="https://biologydictionary.net/wp-content/uploads/2020/06/Partridge-2.jpg"
                alt="login4"
              />
            </ImageContainer>
          </StyledSlider>
        </LoginSlider>
        <LoginForm>{getActiveSection()}</LoginForm>
      </LoginBox>
    </Container>
  )
}

export default Login

const ImageContainer = styled.div`
  width: 400px;
  height: 400px;

  img {
    object-fit: cover;
    object-position: center;
    width: 100%;
    height: 100%;
  }
`

const Container = styled.div`
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)),
    url(${backgroundImage});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`

const LoginBox = styled.div`
  display: flex;
  background-color: #fafafa;
  width: 800px;
  height: 400px;
  border: 1px solid #fafafa;
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 70%;
    height: auto;
  }
`

const LoginSlider = styled.div`
  width: 50%;
  height: 100%;
  background-color: lightcyan;

  @media (max-width: 768px) {
    display: none;
  }
`

const StyledSlider = styled(Carousel)``

const LoginForm = styled.div`
  width: 50%;
  height: 100%;
  padding: 5px;

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
  }
`

const Logo = styled.div`
  width: 300px;
  height: 200px;
  background: url(${logo});
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`
const FormTitle = styled.p`
  margin: 20px 0;
  font-size: 35px;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 16px;
    margin: 5px 0;
  }
`
const StyledExtraText = styled.p`
  margin-bottom: 0;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

const ExtraButtonContainer = styled.div`
  margin-top: 10px;
  display: flex;
`
const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`
const Form = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const ExtraActionsWrapper = styled.div``

const StyledButton = styled(Button)`
  @media (max-width: 768px) {
    font-size: 10px;
  }
`
